/**
 * Logger Utility
 * Structured logging with different levels for debugging and error tracking
 *
 * WHERE TO SEE LOGS:
 * - Development: Browser DevTools Console (F12 -> Console tab)
 * - Production: Browser DevTools Console + can be extended to send to external services
 *
 * Log Levels:
 * - DEBUG: Detailed debugging info (only in development)
 * - INFO: General information about app flow
 * - WARN: Warning messages that don't break functionality
 * - ERROR: Error messages that need attention
 */

export enum LogLevel {
  DEBUG = 'DEBUG',
  INFO = 'INFO',
  WARN = 'WARN',
  ERROR = 'ERROR',
}

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  context: string;
  message: string;
  data?: unknown;
  error?: Error;
}

// Check if we're in development or production
// NOTE: React uses 'development', 'production', or 'test' for NODE_ENV
const isDevelopment = process.env.NODE_ENV === 'development';
const isProduction = process.env.NODE_ENV === 'production';

// Format timestamp for log entries
const getTimestamp = (): string => {
  return new Date().toISOString();
};

// Store logs in memory for debugging (last 100 entries)
const LOG_HISTORY_SIZE = 100;
const logHistory: LogEntry[] = [];

const addToHistory = (entry: LogEntry): void => {
  logHistory.push(entry);
  if (logHistory.length > LOG_HISTORY_SIZE) {
    logHistory.shift();
  }
};

/**
 * Main Logger Object
 *
 * Usage Examples:
 *
 * logger.debug('HeyGenService', 'Initializing avatar session', { companionId: '123' });
 * logger.info('HeyGenService', 'Session started successfully');
 * logger.warn('HeyGenService', 'Connection quality degraded', { quality: 'POOR' });
 * logger.error('HeyGenService', 'Failed to start session', error, { retryCount: 3 });
 */
export const logger = {
  /**
   * DEBUG level - Only logs in development mode
   * Use for detailed debugging information
   */
  debug: (context: string, message: string, data?: unknown): void => {
    if (!isDevelopment) return; // Only log debug in development

    const entry: LogEntry = {
      timestamp: getTimestamp(),
      level: LogLevel.DEBUG,
      context,
      message,
      data,
    };

    addToHistory(entry);
  },

  /**
   * INFO level - Logs in all environments
   * Use for general application flow information
   */
  info: (context: string, message: string, data?: unknown): void => {
    const entry: LogEntry = {
      timestamp: getTimestamp(),
      level: LogLevel.INFO,
      context,
      message,
      data,
    };

    addToHistory(entry);
  },

  /**
   * WARN level - Logs in all environments
   * Use for warnings that don't break functionality
   */
  warn: (context: string, message: string, data?: unknown): void => {
    const entry: LogEntry = {
      timestamp: getTimestamp(),
      level: LogLevel.WARN,
      context,
      message,
      data,
    };

    addToHistory(entry);
  },

  /**
   * ERROR level - Logs in all environments
   * Use for errors that need attention
   *
   * In production, this could be extended to send to:
   * - Sentry, LogRocket, or similar error tracking services
   * - Backend logging endpoint
   */
  error: (context: string, message: string, error?: Error, data?: unknown): void => {
    const entry: LogEntry = {
      timestamp: getTimestamp(),
      level: LogLevel.ERROR,
      context,
      message,
      error,
      data,
    };

    addToHistory(entry);

    // In production, you could send errors to an external service
    if (isProduction) {
      // Example: Send to your backend or error tracking service
      // sendToErrorTrackingService(entry);
    }
  },

  /**
   * Get log history for debugging
   * Access in browser console: window.__APP_LOGS__
   */
  getHistory: (): LogEntry[] => {
    return [...logHistory];
  },

  /**
   * Clear log history
   */
  clearHistory: (): void => {
    logHistory.length = 0;
  },
};

// Expose log history to browser console for debugging
// Access via: window.__APP_LOGS__ in browser DevTools
if (typeof window !== 'undefined') {
  (window as unknown as { __APP_LOGS__: () => LogEntry[] }).__APP_LOGS__ = () =>
    logger.getHistory();
}

export default logger;
